"use client";

import type { GoalFocusTarget } from "@/lib/store";
import type {
  CrossSurfaceConfirmationTruth,
  CrossSurfaceRevealTarget,
} from "@/lib/stores/crossSurfaceConfirmation";

interface GoalFocusHandoffInput {
  goalId: string;
  goalTitle: string;
  message?: string | null;
  nextStep?: string | null;
  source?: GoalFocusTarget["source"];
}

type GoalFocusDestination = "goals" | "schedule-plan";

export function buildGoalFocusTarget(input: GoalFocusHandoffInput): GoalFocusTarget {
  return {
    goalId: input.goalId,
    goalTitle: input.goalTitle,
    headline: `${input.goalTitle} is drifting`,
    message: input.message ?? null,
    nextStep: input.nextStep ?? null,
    source: input.source ?? "growth",
  };
}

export function buildGoalFocusConfirmationTruth(
  input: GoalFocusHandoffInput,
  destination: GoalFocusDestination
): CrossSurfaceConfirmationTruth {
  return {
    targetKind: "goal",
    targetId: input.goalId,
    destinationLayer: destination === "goals" ? "goals" : "plan",
    owningService: "goal_focus",
  };
}

export function buildGoalFocusRevealTarget(
  input: GoalFocusHandoffInput,
  destination: GoalFocusDestination
): CrossSurfaceRevealTarget {
  return {
    surface: destination,
    kind: "goal_focus",
    goalId: input.goalId,
  };
}

export function buildGoalFocusConfirmation(
  input: GoalFocusHandoffInput,
  destination: GoalFocusDestination
) {
  const label = destination === "goals" ? "Open Goals in Review" : "Open Plan";
  const message =
    destination === "goals"
      ? `Recovery is ready in Review · Goals: ${input.goalTitle}.`
      : `A restart block is ready to plan for ${input.goalTitle}.`;

  return {
    message,
    ctaLabel: label,
    ctaTab: destination,
    revealTarget: buildGoalFocusRevealTarget(input, destination),
    truth: buildGoalFocusConfirmationTruth(input, destination),
  };
}
